import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_ICON = { implemented: "✓", in_progress: "◐", not_started: "○" };
const STATUS_LABEL = { implemented: "Implementado", in_progress: "En curso", not_started: "No iniciado" };

export default function Reports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/reports/compliance").then((r) => setReport(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const printPdf = () => { window.print(); };

  if (loading || !report) return <div className="p-8 text-[var(--text-dim)]">Generando reporte…</div>;

  return (
    <>
      <style>{`
        @media print {
          aside, header, [data-testid="cmdk-trigger"], [data-testid="notif-bell"], nav, .no-print { display: none !important; }
          body, main, .App { background: white !important; color: black !important; }
          .print-page { color: black !important; background: white !important; padding: 20mm !important; }
          .print-page * { color: black !important; border-color: #999 !important; }
          .print-badge { background: #eee !important; color: black !important; border: 1px solid #999 !important; }
        }
      `}</style>

      <div className="p-8 space-y-6 print-page" data-testid="report-page">
        <div className="flex items-end justify-between border-b border-[var(--border)] pb-5 no-print">
          <div>
            <div className="label-mono mb-2">EXPORT · COMPLIANCE PDF</div>
            <h1 className="font-display font-black text-4xl tracking-tight">Reporte de Compliance</h1>
            <p className="text-sm text-[var(--text-dim)] mt-1">
              Descarga o imprime este reporte para auditorías (ISO 42001 · EU AI Act · SOC 2).
            </p>
          </div>
          <Button onClick={printPdf} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="report-print">
            <Printer className="w-4 h-4 mr-2" /> Descargar PDF
          </Button>
        </div>

        {/* Print header */}
        <div className="border-b border-[var(--border)] pb-4">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-6 h-6 text-[var(--accent)]" />
            <div>
              <div className="font-display font-black text-2xl">Reporte de Compliance IA</div>
              <div className="label-mono">{report.company_name} · {new Date(report.generated_at).toLocaleString("es-ES")}</div>
            </div>
          </div>
        </div>

        {/* Overall */}
        <div className="grid grid-cols-4 gap-4" data-testid="report-overall">
          <StatBig label="Cobertura Global" value={`${report.overall_coverage}%`} />
          <StatBig label="Agentes Activos" value={report.posture.active_agents} />
          <StatBig label="Docs en Base" value={report.posture.documents_in_kb} />
          <StatBig label="Incidentes" value={report.posture.incidents_total} />
        </div>

        {/* Posture */}
        <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
          <div className="label-mono mb-3">POSTURA OPERATIVA</div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <Kv k="Modo del sistema" v={report.system_mode} />
            <Kv k="Template de industria" v={report.industry_template || "—"} />
            <Kv k="Integraciones activas" v={report.integrations_configured.length} />
            <Kv k="Eventos alto riesgo (30d)" v={report.posture.high_risk_events_30d} />
            <Kv k="Aprobaciones aprobadas" v={`${report.posture.approvals_approved} / ${report.posture.approvals_total}`} />
          </div>
        </div>

        {/* Frameworks */}
        <div className="space-y-4">
          {report.frameworks.map((f) => (
            <div key={f.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`report-fw-${f.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="label-mono mb-1">{f.category}</div>
                  <div className="font-display font-bold text-lg">{f.name}</div>
                </div>
                <div className="text-right">
                  <div className="label-mono">COBERTURA</div>
                  <div className="font-display font-black text-2xl text-[var(--accent)]">{f.coverage}%</div>
                </div>
              </div>
              <div className="h-1 bg-[var(--bg)] mb-4">
                <div className="h-1 bg-[var(--accent)]" style={{ width: `${f.coverage}%` }} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {f.controls.map((c) => (
                  <div key={c.name} className="flex items-center gap-2 text-xs">
                    <span className="font-mono w-4 text-center">{STATUS_ICON[c.status]}</span>
                    <span className="text-white flex-1">{c.name}</span>
                    <span className="font-mono text-[10px] text-[var(--text-dim)]">{STATUS_LABEL[c.status]}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[var(--border)] pt-4 text-xs text-[var(--text-dim)] leading-relaxed">
          Reporte generado automáticamente por Agent Company OS.
          Este documento es informativo. La responsabilidad de cumplimiento normativo recae en la organización.
        </div>
      </div>
    </>
  );
}

const StatBig = ({ label, value }) => (
  <div className="border border-[var(--border)] bg-[var(--surface)] p-4">
    <div className="label-mono mb-2">{label}</div>
    <div className="font-display font-black text-3xl">{value}</div>
  </div>
);

const Kv = ({ k, v }) => (
  <div>
    <div className="label-mono">{k}</div>
    <div className="text-white text-sm font-mono">{v}</div>
  </div>
);
